interface InstallPromptProps {
  isIOS: boolean;
  showIOSInstructions: boolean;
  onInstall: () => void;
  onDismiss: () => void;
  onCloseIOSInstructions: () => void;
}

export default function InstallPrompt({
  isIOS,
  showIOSInstructions,
  onInstall,
  onDismiss,
  onCloseIOSInstructions,
}: InstallPromptProps) {
  // iOS Instructions Modal
  if (showIOSInstructions) {
    return (
      <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 fade-in">
        <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl slide-up safe-area-inset-bottom">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">
              Install RDM Field
            </h3>
            <button
              onClick={onCloseIOSInstructions}
              className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <p className="mb-4 text-gray-600">
            To install this app on your iPhone or iPad:
          </p>

          <ol className="mb-6 space-y-4 text-sm">
            <li className="flex items-start space-x-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs font-semibold">
                1
              </span>
              <div>
                <p className="font-medium text-gray-800">Tap the Share button</p>
                <p className="text-gray-500">
                  Look for the{' '}
                  <svg
                    className="inline-block h-5 w-5 text-primary-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                    />
                  </svg>{' '}
                  icon at the bottom of Safari
                </p>
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs font-semibold">
                2
              </span>
              <div>
                <p className="font-medium text-gray-800">Select "Add to Home Screen"</p>
                <p className="text-gray-500">Scroll down in the share menu to find it</p>
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs font-semibold">
                3
              </span>
              <div>
                <p className="font-medium text-gray-800">Tap "Add"</p>
                <p className="text-gray-500">The app will appear on your home screen</p>
              </div>
            </li>
          </ol>

          <button
            onClick={onCloseIOSInstructions}
            className="w-full rounded-lg bg-primary-600 py-3 font-semibold text-white transition-colors hover:bg-primary-700"
          >
            Got it
          </button>
        </div>
      </div>
    );
  }

  // Standard Install Banner (Android/Desktop)
  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 p-4 safe-area-inset-bottom">
      <div className="mx-auto max-w-md rounded-2xl bg-white p-4 shadow-lg slide-up">
        <div className="flex items-center space-x-4">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary-100">
            <svg
              className="h-7 w-7 text-primary-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-gray-800">Install RDM Field</h4>
            <p className="text-sm text-gray-500">
              {isIOS
                ? 'Add to your home screen for quick access'
                : 'Install for offline access and faster loading'}
            </p>
          </div>
        </div>

        <div className="mt-4 flex space-x-3">
          <button
            onClick={onDismiss}
            className="flex-1 rounded-lg border border-gray-300 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            Not now
          </button>
          <button
            onClick={onInstall}
            className="flex-1 rounded-lg bg-primary-600 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700"
          >
            Install
          </button>
        </div>
      </div>
    </div>
  );
}
